import Phaser from 'phaser';
import { PALETTE } from '../config';
import { t, instructionsFor, disciplineLabel } from '../game/i18n';
import { Button } from './Button';
import { viewport } from './viewport';

const EMOJI: Record<string, string> = {
  sprint_100m: '🏃', long_jump: '🦘', hurdles: '🏃', javelin: '🎯', high_jump: '🤸',
  triple_jump: '🦘', shot_put: '🏋️', hammer_throw: '🔨', pole_vault: '🐱', discus: '🥏',
  weightlifting: '🏋️', swim_50m: '🏊', diving: '🤿', archery: '🏹',
};

/** Premium discipline tile: glass base, the card art cover-fit + rounded, gloss + rim. */
function drawDisciplineTile(scene: Phaser.Scene, layer: Phaser.GameObjects.Container, x: number, y: number, size: number, disciplineId: string): void {
  const r = 18;
  const g = scene.add.graphics();
  g.fillStyle(0x000000, 0.30).fillRoundedRect(x + 2, y + 4, size, size, r);
  g.fillStyle(PALETTE.bgBottom, 1).fillRoundedRect(x, y, size, size, r);
  layer.add(g);
  const key = `card_${disciplineId}`;
  if (scene.textures.exists(key)) {
    const img = scene.add.image(x + size / 2, y + size / 2, key).setOrigin(0.5);
    const src = scene.textures.get(key).getSourceImage();
    img.setScale(Math.max(size / src.width, size / src.height));
    const m = scene.make.graphics({ x: 0, y: 0 });
    m.fillStyle(0xffffff).fillRoundedRect(x, y, size, size, r);
    img.setMask(m.createGeometryMask());
    layer.add(img);
  } else {
    layer.add(scene.add.text(x + size / 2, y + size / 2, EMOJI[disciplineId] ?? '🏅', { fontSize: `${Math.round(size * 0.5)}px` }).setOrigin(0.5));
  }
  const o = scene.add.graphics();
  o.fillStyle(0xffffff, 0.10).fillRoundedRect(x + 3, y + 3, size - 6, size * 0.4, r - 5);
  o.lineStyle(2, 0xffffff, 0.26).strokeRoundedRect(x, y, size, size, r);
  o.lineStyle(2.5, PALETTE.cadence, 0.22).strokeRoundedRect(x - 1.5, y - 1.5, size + 3, size + 3, r + 1);
  layer.add(o);
}

/**
 * "How to play" modal for a discipline. Two-column landscape layout (goal + tip on the
 * left, numbered steps on the right) so text stays large and readable even on short
 * phones — font sizes use absolute minimums, never the tiny height-scaled values.
 * Self-contained: own scrim + panel + close, fixed to camera, destroyed on close.
 */
export function showInstructions(scene: Phaser.Scene, disciplineId: string): void {
  const info = instructionsFor(disciplineId);
  if (!info) return;
  const { w, h, cx, cy, s } = viewport(scene);
  const D = 1000;

  // Readable absolute sizes (s drops to 0.6 on phones, which would shrink text to ~9px).
  const fTitle = Math.max(22, Math.round(26 * s));
  const fGoal = Math.max(16, Math.round(18 * s));
  const fStep = Math.max(16, Math.round(17 * s));
  const fNum = Math.max(15, Math.round(16 * s));
  const fTip = Math.max(15, Math.round(16 * s));
  const fLabel = Math.max(13, Math.round(13 * s));

  const layer = scene.add.container(0, 0).setScrollFactor(0).setDepth(D);
  const scrim = scene.add.rectangle(0, 0, w, h, PALETTE.ink, 0.78).setOrigin(0, 0).setInteractive();
  layer.add(scrim);

  const panelW = Math.min(760, w * 0.94);
  const pad = Math.round(22 * Math.max(0.8, s));
  const headerH = Math.max(54, Math.round(60 * s));
  const colGap = Math.round(22 * Math.max(0.8, s));
  const innerW = panelW - pad * 2;
  const leftW = Math.round(innerW * 0.38);
  const rightW = innerW - leftW - colGap;
  const bodyTop = headerH + pad;

  // --- Right column: numbered steps (built first to size the panel) ---
  const stepEls: Phaser.GameObjects.GameObject[] = [];
  const badge = Math.max(26, Math.round(28 * s));
  let ry = 0;
  const rightHeading = scene.add.text(0, ry, t('instr.howToPlay'), {
    fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fLabel}px`, color: '#59d2ff', fontStyle: 'bold',
  }).setOrigin(0, 0);
  stepEls.push(rightHeading);
  ry += rightHeading.height + 12 * s;
  info.steps.forEach((step, i) => {
    const t = scene.add.text(badge + 12, ry, step, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fStep}px`, color: '#f4f7ff', lineSpacing: 3,
      wordWrap: { width: rightW - badge - 12 },
    }).setOrigin(0, 0);
    const rowH = Math.max(badge, t.height);
    const bg = scene.add.graphics();
    bg.fillStyle(PALETTE.cadence, 1).fillCircle(badge / 2, ry + badge / 2, badge / 2);
    const num = scene.add.text(badge / 2, ry + badge / 2, `${i + 1}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fNum}px`, color: '#0b1020', fontStyle: 'bold',
    }).setOrigin(0.5);
    stepEls.push(bg, num, t);
    ry += rowH + 16 * s;
  });
  const rightH = ry;

  // --- Left column: goal + tip card ---
  const leftEls: Phaser.GameObjects.GameObject[] = [];
  // Reserve space for the discipline tile; it's drawn absolutely after layout (so its rounded
  // image mask lands correctly). Uses the same card art as the carousels — visually coherent.
  const tileSz = Math.max(84, Math.round(100 * s));
  let ly = tileSz + 16 * s;
  const goal = scene.add.text(0, ly, info.goal, {
    fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fGoal}px`, color: '#cfd8f5', fontStyle: 'italic', lineSpacing: 3,
    wordWrap: { width: leftW },
  }).setOrigin(0, 0);
  leftEls.push(goal);
  ly += goal.height + 16 * s;
  // Tip card.
  const tipText = scene.add.text(14 * s, 0, `💡 ${info.tip}`, {
    fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fTip}px`, color: '#bdf0cf', lineSpacing: 3,
    wordWrap: { width: leftW - 28 * s },
  }).setOrigin(0, 0);
  const tipH = tipText.height + 20 * s;
  const tipBg = scene.add.graphics();
  tipBg.fillStyle(PALETTE.good, 0.12).fillRoundedRect(0, ly, leftW, tipH, 12);
  tipBg.lineStyle(1.5, PALETTE.good, 0.4).strokeRoundedRect(0, ly, leftW, tipH, 12);
  tipText.setY(ly + 10 * s);
  leftEls.push(tipBg, tipText);
  const leftH = ly + tipH;

  // --- Panel sizing from the taller column ---
  const bodyH = Math.max(rightH, leftH);
  const footerH = Math.max(56, Math.round(62 * s));
  let panelH = bodyTop + bodyH + 14 * s + footerH + pad;
  panelH = Math.min(panelH, h * 0.96);
  const px = cx - panelW / 2;
  const py = cy - panelH / 2;

  // --- Panel + header background ---
  const g = scene.add.graphics();
  g.fillStyle(PALETTE.ink, 0.5).fillRoundedRect(px - 4, py + 8, panelW + 8, panelH + 8, 22); // shadow
  g.fillStyle(PALETTE.bgTop, 0.99).fillRoundedRect(px, py, panelW, panelH, 20);
  g.fillStyle(PALETTE.laneAccent, 1).fillRoundedRect(px, py, panelW, headerH, { tl: 20, tr: 20, bl: 0, br: 0 });
  const hc = Phaser.Display.Color.IntegerToColor(PALETTE.laneAccent);
  g.fillStyle(hc.clone().lighten(20).color, 0.5).fillRoundedRect(px + 4, py + 4, panelW - 8, headerH * 0.46, { tl: 16, tr: 16, bl: 0, br: 0 });
  g.lineStyle(2, PALETTE.cadence, 0.45).strokeRoundedRect(px, py, panelW, panelH, 20);
  layer.add(g);

  const title = disciplineLabel(disciplineId);
  layer.add(scene.add.text(px + pad, py + headerH / 2, title, {
    fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fTitle}px`, color: '#0b1020', fontStyle: 'bold',
  }).setOrigin(0, 0.5));

  // Close X (top-right of header).
  const xR = Math.max(16, Math.round(18 * s));
  const xc = scene.add.circle(px + panelW - pad - xR / 2, py + headerH / 2, xR, PALETTE.ink, 0.18).setStrokeStyle(2, PALETTE.ink, 0.3);
  const xt = scene.add.text(xc.x, xc.y, '✕', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(16, Math.round(18 * s))}px`, color: '#0b1020', fontStyle: 'bold' }).setOrigin(0.5);
  layer.add(xc); layer.add(xt);

  // Place columns into the layer (translate the pre-built relative positions).
  const leftX = px + pad;
  const rightX = px + pad + leftW + colGap;
  const colTop = py + bodyTop;
  const shift = (e: Phaser.GameObjects.GameObject, dx: number, dy: number): void => {
    const o = e as unknown as { x: number; y: number };
    o.x += dx; o.y += dy; layer.add(e);
  };
  leftEls.forEach((e) => shift(e, leftX, colTop));
  stepEls.forEach((e) => shift(e, rightX, colTop));

  // Discipline art tile at the top of the left column (drawn in absolute coords).
  drawDisciplineTile(scene, layer, leftX, colTop, tileSz, disciplineId);

  // Vertical divider between columns.
  const div = scene.add.graphics();
  div.lineStyle(1.5, PALETTE.trackLine, 0.5).beginPath();
  const divX = px + pad + leftW + colGap / 2;
  div.moveTo(divX, colTop + 4); div.lineTo(divX, py + panelH - footerH - 18 * s); div.strokePath();
  layer.add(div);

  const close = (): void => layer.destroy();
  const btn = new Button(scene, cx, py + panelH - footerH / 2 - pad / 2, t('instr.gotIt'), close, {
    width: Math.min(260, panelW * 0.5), height: Math.max(48, Math.round(54 * s)), fontSize: Math.max(18, Math.round(22 * s)), color: PALETTE.good,
  });
  btn.setScrollFactor(0).setDepth(D + 1);
  layer.add(btn);

  [scrim, xc, xt].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, close));

  layer.setAlpha(0);
  scene.tweens.add({ targets: layer, alpha: 1, duration: 160 });
}
